import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { ROUTES } from '@/constants/routes';
import { getInitials } from '@/utils/stringUtils';

interface ClickableAvatarProps {
  userId: string;
  avatarUrl?: string;
  fullName: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const sizeMap = {
  xs: { box: 'h-6 w-6', text: 'text-[9px]', border: 'border' },
  sm: { box: 'h-8 w-8', text: 'text-xs', border: 'border-2' },
  md: { box: 'h-10 w-10', text: 'text-sm', border: 'border-2' },
  lg: { box: 'h-12 w-12', text: 'text-base', border: 'border-2' },
};

export const ClickableAvatar = ({
  userId,
  avatarUrl,
  fullName,
  className = "",
  size = 'md'
}: ClickableAvatarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { pushBreadcrumb } = useNavigation();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.id === userId) {
      navigate(ROUTES.PROFILE);
    } else {
      const profileRoute = ROUTES.USER_PROFILE(userId);

      if (location.pathname === '/matches') {
        sessionStorage.setItem('lastMatchesPage', 'true');
        pushBreadcrumb(
          profileRoute,
          fullName,
          { userId },
          { sourceMatches: 'Matches', playerName: fullName }
        );
      }

      navigate(profileRoute);
    }
  };

  const { box, text, border } = sizeMap[size];

  return (
    <button
      type="button"
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={handleClick}
      aria-label={`View ${fullName}'s profile`}
    >
      <Avatar className={`cursor-pointer hover:scale-105 transition-transform ${box} ${className}`}>
        <AvatarImage src={avatarUrl} alt="" />
        <AvatarFallback className={`bg-primary/15 text-primary font-semibold ${text} ${border} border-primary/20`}>
          {getInitials(fullName)}
        </AvatarFallback>
      </Avatar>
    </button>
  );
};